import React, { Fragment, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { shallowEqual, useSelector } from 'react-redux';
import { Popover } from '@mui/material';

import { InputField, SelectOptions } from 'components/ui/controls/SearchTickets';
import { Divider, RegularWrapper, Text } from 'components/common';
import {
  Wrapper,
  Title,
  Block,
  ContentTitle,
  InputWrapper,
  DeleteButton,
  AddButton,
  InstructionInfoWrapper,
} from './PassportData.styles';
import { getDocumentPlaceholder } from 'utils/getDocumentPlaceholder';
import { getDocumentTypesData } from 'reactStore/selectors/documentTypesSelector';
import { RegistrationInfoIcon } from 'assets/icons';
import InstructionInfo from './InstructionInfo/InstructionInfo';

const PassportData = ({ value, onChange, errors }) => {
  const [currentValue] = value;
  const [error] = errors;
  const [anchorEl, setAnchorEl] = useState(null);

  const { t } = useTranslation();
  const [docsAmount, setDocsAmount] = useState(currentValue?.documents_attributes?.length);
  const documentsData = useSelector(getDocumentTypesData, shallowEqual);

  const onChangeDocField = (field, index) => (e) => {
    const currentValue = e?.target ? e.target.value : e;

    if (field === '_type') {
      onChange([
        {
          ...value[0],
          documents_attributes: value[0]?.documents_attributes.reduce((result, item, attributeIndex) => {
            if (attributeIndex === index) {
              return [
                ...result,
                {
                  ...item,
                  [field]: currentValue?.trim(),
                  number: '',
                  elapsed_time: '',
                },
              ];
            }
            return [...result, item];
          }, []),
        },
      ]);
    } else {
      onChange([
        {
          ...value[0],
          documents_attributes: value[0]?.documents_attributes.reduce((result, item, attributeIndex) => {
            if (attributeIndex === index) {
              return [
                ...result,
                {
                  ...item,
                  [field]: currentValue?.trim(),
                },
              ];
            }
            return [...result, item];
          }, []),
        },
      ]);
    }
  };

  const docKeys = ['elapsed_time', 'first_name', 'last_name', 'middle_name', 'number', '_type'];

  const removeDoc = (index) => {
    if (docsAmount > 1) {
      onChange([
        {
          ...value[0],
          documents_attributes: [...value[0].documents_attributes].reduce((result, item, reduceIndex) => {
            if (index === reduceIndex) {
              if (docKeys.some((key) => !item[key])) {
                setDocsAmount((prevState) => prevState - 1);
                return result;
              }
              setDocsAmount((prevState) => prevState - 1);
              return [...result, { ...item, _destroy: 1 }];
            }
            return [...result, item];
          }, []),
        },
      ]);
    }
  };

  const addDoc = () => {
    onChange([
      {
        ...value[0],
        documents_attributes: [
          ...value[0].documents_attributes,
          {
            _type: '',
            number: '',
            elapsed_time: '',
            last_name: '',
            first_name: '',
            middle_name: '',
          },
        ],
      },
    ]);
    setDocsAmount((prevState) => prevState + 1);
  };

  const getDocumentNumberMask = (document) => {
    if (document?._type === 'RussianPassport') {
      return '9999 99-99-99';
    }
    return '';
  };

  const onClose = () => {
    setAnchorEl(null);
  };

  const renderDocuments = useMemo(() => {
    return currentValue?.documents_attributes?.map(
      (item, index) =>
        !item._destroy && (
          <Fragment key={index}>
            {index !== 0 && <Divider />}
            <Block>
              <ContentTitle>
                {t('Settings.Person.Card.IdentityDocument')} {index + 1}
              </ContentTitle>
              <RegularWrapper gap="12px" margin="20px 0 0 0">
                <InputWrapper>
                  <InputField
                    label={t('Settings.Person.Card.LastName')}
                    placeholder="Иванов"
                    onChange={onChangeDocField('last_name', index)}
                    value={currentValue?.documents_attributes?.[index]?.last_name}
                    error={error?.documents_attributes?.[index]?.last_name}
                  />
                </InputWrapper>
                <InputWrapper>
                  <InputField
                    label={t('Settings.Person.Card.FirstName')}
                    placeholder="Иван"
                    onChange={onChangeDocField('first_name', index)}
                    value={currentValue?.documents_attributes?.[index]?.first_name}
                    error={error?.documents_attributes?.[index]?.first_name}
                  />
                </InputWrapper>
                <InputWrapper>
                  <InputField
                    label={t('Settings.Person.Card.MiddleName')}
                    placeholder="Иванович"
                    onChange={onChangeDocField('middle_name', index)}
                    value={currentValue?.documents_attributes?.[index]?.middle_name}
                    error={error?.documents_attributes?.[index]?.middle_name}
                  />
                </InputWrapper>
              </RegularWrapper>
              <RegularWrapper gap="12px" align="center" margin="16px 0 0 0">
                <InputWrapper>
                  <SelectOptions
                    label="Документ"
                    onChange={onChangeDocField('_type', index)}
                    options={documentsData}
                    error={error?.documents_attributes?.[index]?._type}
                    value={currentValue?.documents_attributes?.[index]?._type}
                  />
                </InputWrapper>
                <InputWrapper>
                  <InputField
                    mask={getDocumentNumberMask(currentValue?.documents_attributes?.[index])}
                    placeholder={
                      getDocumentPlaceholder(currentValue?.documents_attributes?.[index], 'number') || 'XXXX XX-XX-XX'
                    }
                    error={error?.documents_attributes?.[index]?.number}
                    value={currentValue?.documents_attributes?.[index]?.number}
                    onChange={onChangeDocField('number', index)}
                    label={t('Settings.Person.Card.SeriesAndNumber')}
                  />
                </InputWrapper>
                {currentValue?.documents_attributes?.[index]?._type !== 'RussianPassport' && (
                  <InputWrapper>
                    <InputField
                      onChange={onChangeDocField('elapsed_time', index)}
                      value={currentValue?.documents_attributes?.[index]?.elapsed_time}
                      error={error?.documents_attributes?.[index]?.elapsed_time}
                      placeholder={getDocumentPlaceholder(value, 'elapsed_time') || 'ДД.ММ.ГГГГ'}
                      mask="99.99.9999"
                      label={t('Settings.Person.Card.ValidUntil')}
                    />
                  </InputWrapper>
                )}
                <DeleteButton type="submit" onClick={() => removeDoc(index)} disabled={docsAmount === 1}>
                  {t('Settings.Person.Card.Delete')}
                </DeleteButton>
              </RegularWrapper>
            </Block>
          </Fragment>
        ),
    );
  }, [value, documentsData, error]);

  return (
    <Wrapper>
      <RegularWrapper>
        <Title>{t('Settings.Person.Card.PassportData')}</Title>
        <InstructionInfoWrapper onClick={(event) => setAnchorEl(event.currentTarget)}>
          <RegistrationInfoIcon />
          <Text size="14px" lineHeight="24px" marginLeft="8px" color="#0079C3" fontWeight="500">
            Инструкция по заполнению
          </Text>
        </InstructionInfoWrapper>
      </RegularWrapper>
      <Popover
        id={!!anchorEl ? 'simple-popover' : null}
        open={!!anchorEl}
        anchorEl
        onClose={onClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <InstructionInfo onClick={onClose} />
      </Popover>
      {renderDocuments}
      <Divider />
      <RegularWrapper margin="24px 0 0 0">
        <AddButton variant="primary" type="submit" small disabled={docsAmount >= 3} onClick={addDoc}>
          Добавить документ
        </AddButton>
      </RegularWrapper>
    </Wrapper>
  );
};

PassportData.propTypes = {
  onChange: PropTypes.func,
  value: PropTypes.array,
  errors: PropTypes.array,
};

export default PassportData;
